import {
	createContext,
	type ReactNode,
	useContext,
	useEffect,
	useMemo,
} from 'react'
import { setAuthToken } from '@/api/mutator/custom-instance'
import { useGetMeBackstage } from '@/generated/api/backstage'
import type { UserPermissionResponse } from '@/generated/api/backstage.schemas'

export interface SessionUser {
	id?: string
	firstName?: string | null
	lastName?: string | null
	email?: string | null
	logonName?: string | null
}
export interface UserSessionValue {
	user?: SessionUser | null
	permissions: UserPermissionResponse[]
	loginRequired: boolean
	isReady: boolean
}

const UserSessionContext = createContext<UserSessionValue>({
	permissions: [],
	loginRequired: false,
	isReady: false,
})

function useMeQuery(enabled: boolean) {
	const {
		data: permissions,
		error,
		isLoading,
	} = useGetMeBackstage({
		query: {
			enabled,
			retry: (failureCount, queryError) =>
				!isUnauthorizedError(queryError) && failureCount < 3,
		},
	})
	return { permissions, error, isLoading }
}

function isUnauthorizedError(error: unknown): boolean {
	if (!error || typeof error !== 'object') return false

	const candidate = error as {
		status?: number
		response?: { status?: number }
	}

	return candidate.status === 401 || candidate.response?.status === 401
}

export function UserSessionProvider({
	children,
	authReady,
	accessToken,
}: Readonly<{
	children: ReactNode
	authReady: boolean
	accessToken?: string
}>) {
	useEffect(() => {
		setAuthToken(accessToken)
	}, [accessToken])

	const canBootstrapSession = authReady && Boolean(accessToken)
	const { permissions = [], error, isLoading } = useMeQuery(canBootstrapSession)
	const loginRequired = !isLoading && isUnauthorizedError(error)
	const isReady = authReady && (!canBootstrapSession || !isLoading)

	const value = useMemo(
		() => ({ permissions, loginRequired, isReady }),
		[permissions, loginRequired, isReady],
	)

	return (
		<UserSessionContext.Provider value={value}>
			{children}
		</UserSessionContext.Provider>
	)
}

export function useUserSession() {
	return useContext(UserSessionContext)
}
